import { createCustomerPortalSession } from "@/api/privateApi";
import { saveSquadIcon } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { Subscription, userSubscriptionSelector } from "@/redux/user/userSlice";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { showModal } from "../../../redux/message/messageSlice";
import { AppDispatch } from "../../../redux/store";
import { BaseModal } from "./BaseModal";
import { LoginSection } from "./SpecificModals/LoginSection";
import { SaveSquadSection } from "./SpecificModals/SaveSquadSection";
import ShareSquadSection from "./SpecificModals/ShareSquadSection";

export type ModalType =
  | "premium-feature"
  | "sign-in-to-subscribe"
  | "already-subscriber"
  | "login-required"
  | "welcome"
  | "welcome-back"
  | "share-squad"
  | "save-squad";

export type Modal = {
  modalType: ModalType;
  premiumFeatureHook?: premiumFeatureHooks;
  freeFeatureLoggedInRequiredHook?: freeFeatureLoggedInRequiredHooks;
  data?: any; // Generic data field for modal-specific data
};

// Potentially modify URL in order to hide ads

type freeFeatureLoggedInRequiredHooks =
  | "upvote/downvote-sbc"
  | "hide-sbc"
  | "favorite-sbc"
  | "complete-sbc"
  | "player-inclusion"
  | "player-exclusion"
  | "complete-evo"
  | "favorite-tactic";

type premiumFeatureHooks =
  | "build-squad-with-club"
  | "solve-sbc-with-club"
  | "see-owned-players"
  | "see-owned-evo-players"
  | "import-club";

const freeFeatureLoggedInRequiredHooks: Record<
  freeFeatureLoggedInRequiredHooks,
  { description: string }
> = {
  "upvote/downvote-sbc": {
    description: "In order to upvote or downvote an SBC, you need to login.",
  },
  "hide-sbc": {
    description: "In order to hide an SBC, you need to login.",
  },
  "favorite-sbc": {
    description: "In order to mark an SBC as favorite, you need to login.",
  },
  "complete-sbc": {
    description: "In order to mark an SBC as completed, you need to login.",
  },
  "player-inclusion": {
    description:
      "In order to force include players into an SBC, you need to login.",
  },
  "player-exclusion": {
    description: "In order to exclude players from an SBC, you need to login.",
  },
  "complete-evo": {
    description:
      "In order to mark an evolution as complete, you need to login.",
  },
  "favorite-tactic": {
    description: "In order to mark a tactic as favorite, you need to login.",
  },
};

const getPremiumFeatureHooks = (
  premiumFeatureHook: premiumFeatureHooks,
  currentSubscription?: Subscription,
): {
  description: string;
  title?: string;
  learnMoreLink?: string;
} => {
  switch (premiumFeatureHook) {
    case "build-squad-with-club":
      return {
        // title: "Build meta squads with your own players",
        description:
          currentSubscription === "gold"
            ? "Upgrade to Ultimate to build meta squads with your own players"
            : "Become a part of the community and build meta squads with your own players",
      };
    case "solve-sbc-with-club":
      return {
        // title: "Import your club to solve SBCs faster ⚡️",
        description:
          "Become a part of the community and solve SBCs with your own players",
        learnMoreLink: "/get-sbc-solver",
      };
    case "see-owned-players":
      return {
        description:
          "In order to see your owned players and their meta ratings, you need to become a subscriber",
        learnMoreLink: "/get-my-players",
      };
    case "see-owned-evo-players":
      return {
        description:
          "In order to see your owned evo players and their meta ratings, you need to become a subscriber",
        learnMoreLink: "/get-my-evolutions",
      };
    case "import-club":
      return {
        description:
          "In order to import your club, you need to become a subscriber",
      };
    default:
      return {
        description:
          "Become a part of the community and unlock exclusive features and benefits.",
      };
  }
};

export const ModalController = ({
  modalType,
  premiumFeatureHook,
  freeFeatureLoggedInRequiredHook,
  data,
}: Modal) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [clickedPositiveAction, setClickedPositiveAction] = useState(false);
  const userSubscription = useSelector(userSubscriptionSelector);

  const closeModal = () => {
    dispatch(showModal());
  };

  const MODAL_CONFIGS = {
    "premium-feature": {
      title:
        getPremiumFeatureHooks(premiumFeatureHook, userSubscription)?.title ??
        "You discovered an Ultimate feature",
      description: premiumFeatureHook
        ? getPremiumFeatureHooks(premiumFeatureHook, userSubscription)
            ?.description
        : "Become a part of the community and unlock exclusive features and benefits.",
      type: "action",
      onClose: closeModal,
      actions: [
        { label: "Close", onClick: closeModal, variant: "negative" },
        {
          label: "Buy Now",
          onClick: () => {
            if (userSubscription === "gold") {
              createCustomerPortalSession();
            } else {
              navigate("/subscription");
              closeModal();
            }
          },
          variant: "positive",
        },
        premiumFeatureHook &&
          getPremiumFeatureHooks(premiumFeatureHook)?.learnMoreLink && {
            onClick: () => {
              navigate(
                getPremiumFeatureHooks(premiumFeatureHook)?.learnMoreLink,
                {
                  state: { from: window.location.pathname },
                },
              );
              closeModal();
            },
            variant: "learn-more",
          },
      ],
    },
    "sign-in-to-subscribe": {
      title: "Ready to unlock your journey?",
      description:
        "You need to sign in to continue. Don't worry, we will take you to payments straight after.",
      actions: [
        { label: "Close", onClick: closeModal, variant: "negative" },
        { label: "Sign in", onClick: () => {}, variant: "positive" },
      ],
      children: (
        <LoginSection onClose={closeModal} type="sign-in-to-subscribe" />
      ),
      className: "px-8 mobile:px-8 h-120",
      onClose: closeModal,
      showCloseButton: true,
    },
    "login-required": {
      title: "Create a Free account",
      description:
        freeFeatureLoggedInRequiredHooks[freeFeatureLoggedInRequiredHook]
          ?.description || "In order to use this feature, you need to login",
      actions: [{ label: "Close", onClick: closeModal, variant: "negative" }],
      children: <LoginSection onClose={closeModal} type="login" />,
      className: "px-8 mobile:px-8 h-114",
      onClose: closeModal,
      showCloseButton: true,
    },
    "already-subscriber": {
      title: "You are already a subscriber",
      content:
        "It looks like you are already an EasySBC subscriber. Thank you for supporting us.",
      type: "action",
      actions: [
        { label: "Close", onClick: closeModal, variant: "negative" },
        {
          label: "Go to main page",
          onClick: () => {
            navigate("/");
            closeModal();
          },
          variant: "positive",
        },
      ],
      onClose: closeModal,
    },
    "share-squad": {
      title: "Share your squad",
      type: "action",
      onClose: closeModal,
      children: <ShareSquadSection squadData={data} />,
      showCloseButton: true,
    },
    "save-squad": {
      title: "Save your squad",
      type: "action",
      actions: !clickedPositiveAction
        ? [
            { label: "Close", onClick: closeModal, variant: "negative" },
            {
              label: "Save",
              onClick: () => setClickedPositiveAction(true),
              variant: "positive",
            },
          ]
        : [{ label: "Close", onClick: closeModal, variant: "negative" }],
      titleIcon: saveSquadIcon,
      onClose: closeModal,
      children: (
        <SaveSquadSection
          clickedSave={clickedPositiveAction}
          onClose={closeModal}
          squadData={data}
        />
      ),
      className: "w-[330px] mobile:w-[330px]",
      notCentered: true,
    },
  };

  if (!modalType) return null;

  return (
    <div
      className={cn(
        "fixed top-0 left-0 right-0 bottom-0 w-full h-full bg-black/60 z-50 flex justify-center items-center",
        MODAL_CONFIGS[modalType]?.notCentered
          ? "items-start pt-8 mobile:pt-16 large-desktop:pt-24"
          : "items-center top-0",
      )}
    >
      <BaseModal
        title={MODAL_CONFIGS[modalType]?.title}
        titleIcon={MODAL_CONFIGS[modalType]?.titleIcon}
        description={MODAL_CONFIGS[modalType]?.description}
        type={MODAL_CONFIGS[modalType]?.type}
        actions={MODAL_CONFIGS[modalType]?.actions}
        className={MODAL_CONFIGS[modalType]?.className}
        onClose={MODAL_CONFIGS[modalType]?.onClose}
        showCloseButton={MODAL_CONFIGS[modalType]?.showCloseButton}
      >
        {MODAL_CONFIGS[modalType]?.children}
      </BaseModal>
    </div>
  );
};
